import { useState } from 'react';
import type { StepProps } from '../../OBWWizard';

// ADR-076 Option 2 — staff fork of pa-prefill. Unlike the subscriber step (which
// fetches GET /portal/pow/prefill-token from the caller's OWN JWT), here the
// PICKED subscriber's identity + prefill were resolved by pa-pick-subscriber and
// seeded into the journey (data.powToken / customerNumber / email / businessName /
// websiteUrl). This step confirms the business-facing fields on the picked
// subscriber's behalf — no subscriber session, no token sniffing.

export function PartnerPrefillConfirmStep({ data, onNext }: StepProps) {
  const customerNumber = (data?.customerNumber as string) ?? '';
  const email = (data?.email as string) ?? '';
  const [businessName, setBusinessName] = useState<string>((data?.businessName as string) ?? '');
  const [websiteUrl, setWebsiteUrl] = useState<string>((data?.websiteUrl as string) ?? '');
  const [urlError, setUrlError] = useState<string | null>(null);

  // POW-C — normalise bare domains ("yourbusiness.com" / "www.foo.com") to the
  // canonical https://host the server-side validateApplicantUrl expects. Mirrors
  // the subscriber fork + the shared domain-verify normaliser.
  function normaliseWebsite(input: string): string | null {
    let raw = input.trim().toLowerCase();
    if (!raw) return null;
    if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`;
    let host: string;
    try { host = new URL(raw).hostname; } catch { return null; }
    const bare = host.replace(/^www\./, '');
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(bare)) return null;
    return `https://${bare}`;
  }

  function commit() {
    const norm = normaliseWebsite(websiteUrl);
    if (!norm) { setUrlError('That doesn\'t look like a valid website domain.'); return; }
    if (!businessName.trim()) return;
    setWebsiteUrl(norm);
    onNext({ businessName: businessName.trim(), websiteUrl: norm });
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Confirm the subscriber's details</h2>
      <p className="obw-subtitle">
        Pre-filled from the selected subscriber account. Customer number and email are tied to their
        account; confirm or correct the business-facing fields.
      </p>

      <p className="obw-form-section">Read-only — from the subscriber account</p>
      <div className="obw-form-grid-2">
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="pa-cn">Customer number</label>
          <input id="pa-cn" className="obw-form-input" type="text" value={customerNumber} readOnly aria-readonly="true"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-dim)' }} />
        </div>
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="pa-email">Email</label>
          <input id="pa-email" className="obw-form-input" type="email" value={email} readOnly aria-readonly="true"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-dim)' }} />
        </div>
      </div>

      <p className="obw-form-section">Confirm — these go on the partner application</p>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="pa-businessName">Business name *</label>
        <input id="pa-businessName" className="obw-form-input" type="text" value={businessName}
          onChange={(e) => setBusinessName(e.target.value)} maxLength={200} required />
      </div>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="pa-websiteUrl">Primary website *</label>
        <input id="pa-websiteUrl" className="obw-form-input" type="text" inputMode="url" value={websiteUrl}
          onChange={(e) => { setWebsiteUrl(e.target.value); setUrlError(null); }}
          onBlur={() => {
            if (!websiteUrl.trim()) return;
            const norm = normaliseWebsite(websiteUrl);
            if (norm) { setWebsiteUrl(norm); setUrlError(null); }
            else setUrlError('That doesn\'t look like a valid website domain.');
          }} placeholder="yourbusiness.com" required />
        {urlError && <p className="obw-form-hint" style={{ color: 'var(--danger)' }}>{urlError}</p>}
      </div>

      <div className="portal-wizard-nav">
        <button type="button" className="obw-btn-primary" onClick={commit}
          disabled={!businessName.trim() || !websiteUrl.trim()}>
          Continue →
        </button>
      </div>
    </div>
  );
}
